/**
 * SystemMonitor - System Monitoring Dashboard
 * Migrated from src/app/components/admin/SystemMonitor.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import SkeletonLoaderComponents from '../components/shared/SkeletonLoader';
import { API_ENDPOINTS } from '../config/api-endpoints';

const { DashboardSkeleton } = SkeletonLoaderComponents;

const SystemMonitor = () => {
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSystemHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      // CRITICAL: Use idToken (not accessToken) for admin authorization
      // cognito:groups claim is ONLY in ID token, NOT in access token
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.SYSTEM_MONITOR}/admin/system/health`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`System health check failed: ${response.status}`);
      }

      const data = await response.json();
      setSystemHealth(data.health || data || {});
    } catch (err) {
      console.error('[SystemMonitor] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSystemHealth();
    const interval = setInterval(fetchSystemHealth, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchSystemHealth]);

  const getHealthColor = (status) => {
    switch (status) {
      case 'healthy': return '#22c55e';
      case 'degraded': return '#f59e0b';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  if (loading && !systemHealth) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="system-monitor">
      <h2>System Monitor</h2>

      {error && (
        <div className="admin-alert admin-alert--warning">
          <span>⚠️</span> {error}
          <div className="admin-btn-group">
            <button className="admin-btn admin-btn--sm" onClick={fetchSystemHealth}>
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">System Health</h3>
        </div>
        <div className="system-monitor__health-list">
          <div className="system-monitor__health-item">
            <div className="system-monitor__health-indicator" style={{ background: getHealthColor(systemHealth?.api || (error ? 'error' : 'unknown')) }}></div>
            <div className="system-monitor__health-label">API Services</div>
            <div className="system-monitor__health-status">{systemHealth?.api || (error ? 'unavailable' : 'checking...')}</div>
          </div>
          <div className="system-monitor__health-item">
            <div className="system-monitor__health-indicator" style={{ background: getHealthColor(systemHealth?.database || (error ? 'error' : 'unknown')) }}></div>
            <div className="system-monitor__health-label">Database</div>
            <div className="system-monitor__health-status">{systemHealth?.database || (error ? 'unavailable' : 'checking...')}</div>
          </div>
          <div className="system-monitor__health-item">
            <div className="system-monitor__health-indicator" style={{ background: getHealthColor(systemHealth?.payments || (error ? 'error' : 'unknown')) }}></div>
            <div className="system-monitor__health-label">Payment Processing</div>
            <div className="system-monitor__health-status">{systemHealth?.payments || (error ? 'unavailable' : 'checking...')}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemMonitor;
